import { useTitle } from "@/hooks/use-title.hook.ts";
import { PerformanceReviewEditSidebar } from "@/pages/talent/performance-reviews/edit/components/PerformanceReviewEditSidebar.tsx";
import { usePerformanceReviewEditPage } from "@/pages/talent/performance-reviews/edit/pages/performance-review-edit-pages.tsx";
import { PerformanceReviewTitleSlot } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewTitleSlot.tsx";
import { PerformanceReviewEditConfigureFormsPage } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewEditConfigureFormsPage.tsx";
import { PerformanceReviewEditDetailsPage } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewEditDetailsPage.tsx";
import { PerformanceReviewEditFinalPage } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewEditFinalPage.tsx";
import { PerformanceReviewEditNotificationsPage } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewEditNotificationsPage.tsx";
import { PerformanceReviewEditRatingsPage } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewEditRatingsPage.tsx";
import { PerformanceReviewEditReviewerTypeFormPage } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewEditReviewerTypeFormPage.tsx";
import { PerformanceReviewEditReviewerTypesPage } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewEditReviewerTypesPage.tsx";
import { PerformanceReviewEditSettingsPage } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewEditSettingsPage.tsx";
import { PerformanceReviewEditInvitationPage } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewEditInvitationPage.tsx";
import { PerformanceReviewEditPageStep } from "@/pages/talent/performance-reviews/edit/performance-review-edit.ts";
import { usePerformanceReviewSetup } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { Button, FullScreenPage, Skeleton } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

export const PerformanceReviewEditPage = () => {
  const { id, step, type_id } = useParams();
  const { t } = useTranslation();
  const { exit } = usePerformanceReviewEditPage();
  const navigate = useNavigate();

  const { data: review } = usePerformanceReviewSetup(id ? parseInt(id) : undefined, !!id);

  useTitle(review?.title, t("performance_review:performance_reviews"));

  const currentStep = useMemo(() => {
    if (step) {
      return step as PerformanceReviewEditPageStep;
    }
    return PerformanceReviewEditPageStep.details;
  }, [step]);

  if (review) {
    if (currentStep == PerformanceReviewEditPageStep.details) {
      return <PerformanceReviewEditDetailsPage review={review} />;
    } else if (currentStep == PerformanceReviewEditPageStep.reviewers) {
      return <PerformanceReviewEditReviewerTypesPage review={review} />;
    } else if (currentStep == PerformanceReviewEditPageStep.forms) {
      if (type_id) {
        const reviewerType = review.reviewer_types.find((type) => type.id === parseInt(type_id));
        if (reviewerType)
          return (
            <PerformanceReviewEditReviewerTypeFormPage
              review={review}
              reviewerType={reviewerType}
            />
          );
      }
      return <PerformanceReviewEditConfigureFormsPage review={review} />;
    } else if (currentStep == PerformanceReviewEditPageStep.settings) {
      return <PerformanceReviewEditSettingsPage review={review} />;
    } else if (currentStep == PerformanceReviewEditPageStep.invitation) {
      return <PerformanceReviewEditInvitationPage review={review} />;
    } else if (currentStep == PerformanceReviewEditPageStep.ratings) {
      return <PerformanceReviewEditRatingsPage review={review} />;
    } else if (currentStep == PerformanceReviewEditPageStep.notifications) {
      return <PerformanceReviewEditNotificationsPage review={review} />;
    } else if (currentStep == PerformanceReviewEditPageStep.final) {
      return <PerformanceReviewEditFinalPage review={review} />;
    }
  }

  return (
    <FullScreenPage
      backSlot={
        <Button
          variant={"secondary"}
          size={"lg"}
          onClick={() => (review ? exit(review.id) : navigate(`/performance-reviews`))}
        >
          {t("common:exit")}
        </Button>
      }
      titleSlot={<PerformanceReviewTitleSlot step={PerformanceReviewEditPageStep.details} />}
      isCenterVertical={false}
      sidebarSlot={<PerformanceReviewEditSidebar step={PerformanceReviewEditPageStep.details} />}
    >
      <Skeleton className={"w-full h-10"} />
    </FullScreenPage>
  );
};
